import React, { useEffect, useRef } from 'react'
import { Zap, Sparkles } from 'lucide-react'
import type { BatchProgress, BatchTranslateResult, BatchMTProvider } from '@/lib/cat/batchMTService'

const PROVIDER_NAMES: Record<BatchMTProvider, string> = {
  deepl: 'DeepL', google: 'Google Translate', mymemory: 'MyMemory'
}

interface Props {
  isOpen: boolean
  progress: BatchProgress | null
  result: BatchTranslateResult | null
  provider: BatchMTProvider
  onCancel: () => void
  onDone: () => void
  onAIReview?: () => void
}

const BatchTranslateProgress: React.FC<Props> = ({
  isOpen, progress, result, provider, onCancel, onDone, onAIReview
}) => {
  const logRef = useRef<string[]>([])
  const logContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (progress?.message && progress.status === 'running') {
      logRef.current = [...logRef.current, progress.message].slice(-15)
      if (logContainerRef.current)
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [progress?.message])

  useEffect(() => { if (isOpen && !result) logRef.current = [] }, [isOpen])

  if (!isOpen) return null

  const isComplete   = result !== null
  const isCancelled  = result?.error === 'Cancelled'
  const isError      = isComplete && !result.success && !isCancelled
  const pct          = progress?.percentage ?? (isComplete ? 100 : 0)
  const barColor     = isCancelled ? 'bg-gray-400' : isError ? 'bg-red-500' : isComplete ? 'bg-emerald-500' : 'bg-gold-500'
  const formatTime   = (ms: number) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
  const headerIcon   = isComplete ? (isCancelled ? '⛔' : isError ? '❌' : '✅') : '⚡'
  const headerTitle  = isComplete
    ? (isCancelled ? 'Cancelled' : isError ? 'Translation Failed' : 'Translation Complete!')
    : `Translating with ${PROVIDER_NAMES[provider]}…`

  const showAIReview = isComplete && !isCancelled && !isError && !!onAIReview

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
          <span className={`text-2xl ${!isComplete ? 'animate-pulse' : ''}`}>{headerIcon}</span>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">{headerTitle}</h2>
            {!isComplete && <p className="text-sm text-gray-500">Batch {progress?.currentBatch ?? 1}</p>}
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">
                {isComplete
                  ? `${result?.segmentsTranslated ?? 0} segments translated`
                  : `${progress?.completed ?? 0} / ${progress?.total ?? 0} segments`}
              </span>
              <span className="font-semibold text-slate-800">{pct}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div className={`${barColor} h-2.5 rounded-full transition-all duration-300`}
                style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Log */}
          <div ref={logContainerRef}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 h-20 overflow-y-auto text-xs font-mono">
            {logRef.current.length === 0
              ? <p className="text-gray-400">Starting…</p>
              : logRef.current.map((msg, i) => (
                <p key={i} className={i === logRef.current.length - 1 ? 'text-slate-800' : 'text-gray-400'}>
                  {i === logRef.current.length - 1 ? '▶ ' : '✓ '}{msg}
                </p>
              ))}
          </div>

          {/* Completion stats */}
          {isComplete && result && !isCancelled && !isError && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Segments done',    value: result.segmentsTranslated,          color: 'text-emerald-600' },
                { label: 'Characters used',  value: result.charactersUsed.toLocaleString(), color: 'text-slate-800' },
                { label: 'Time taken',       value: formatTime(result.timeTakenMs),      color: 'text-slate-800' },
                { label: 'Failed',           value: result.failedSegments.length,
                  color: result.failedSegments.length > 0 ? 'text-amber-600' : 'text-emerald-600' },
              ].map((s, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center">
                  <p className={`font-bold text-lg ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {isComplete && result && result.failedSegments.length > 0 && (
            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
              <span>⚠️</span>
              <p>{result.failedSegments.length} segment(s) could not be translated. Try again individually.</p>
            </div>
          )}

          {isError && result?.error && (
            <div className="flex items-start gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
              <span>❌</span>
              <p className="break-all">{result.error}</p>
            </div>
          )}

          {/* AI Review callout */}
          {showAIReview && (
            <div className="flex items-start gap-3 bg-gold-50 border border-gold-200 rounded-lg px-4 py-3">
              <Sparkles className="h-4 w-4 text-gold-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gold-900">Review with TranslyxAI</p>
                <p className="text-xs text-gold-700 mt-0.5">
                  AI will scan all {result?.segmentsTranslated ?? 0} translated segments for meaning shifts,
                  omissions, and tone issues — and flag any that need attention.
                </p>
              </div>
            </div>
          )}

          {isComplete && !isError && !isCancelled && (
            <p className="text-gray-400 text-xs text-center">
              <Zap className="h-3 w-3 inline mr-1 text-gold-500" />
              Review and edit translations, then confirm each segment to save to TM.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          {!isComplete ? (
            <button onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 hover:border-gray-400 rounded-lg transition-all">
              Cancel
            </button>
          ) : (
            <>
              {showAIReview && (
                <button onClick={onAIReview}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg transition-colors">
                  <Sparkles className="h-4 w-4 text-gold-400" />
                  Review with AI
                </button>
              )}
              <button onClick={onDone}
                className="flex items-center gap-2 px-5 py-2 bg-gold-500 hover:bg-gold-600 text-white text-sm font-semibold rounded-lg transition-colors">
                {isCancelled || isError ? 'Close' : 'Done — Review Translations'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BatchTranslateProgress
